"use client"

import { usePathname } from "next/navigation"
import { useCallback, useEffect, useRef } from "react"

const STORAGE_KEY = "scroll-positions"
const MAX_ENTRIES = 10

let _isPopstateNavigation = false
if (typeof window !== "undefined") {
    window.addEventListener("popstate", () => {
        _isPopstateNavigation = true
    })
}

type ScrollPositions = Record<string, number>

function readPositions(): ScrollPositions {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    let parsed: ScrollPositions = {}
    try {
        parsed = JSON.parse(raw)
    } catch {
        return {}
    }
    return parsed
}

function writePosition(path: string, y: number) {
    const positions = readPositions()
    delete positions[path]
    positions[path] = y

    const keys = Object.keys(positions)
    if (keys.length > MAX_ENTRIES) {
        delete positions[keys[0]]
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions))
}

function readPosition(path: string): number | null {
    return readPositions()[path] ?? null
}

function removePosition(path: string) {
    const positions = readPositions()
    delete positions[path]
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions))
}

type UseScrollRestoreOptions = {
    /** Debounce interval for persisting scroll position (ms) */
    debounceMs?: number
    /** Scroll behavior */
    behavior?: ScrollBehavior
}

/**
 * Persists scroll position to a capped sessionStorage map and restores it
 * on remount. The map holds at most 10 entries (FIFO eviction).
 *
 * Returns `preserveScroll` — wrap any async callback that mutates content
 * (load-more, load-previous) to pin the viewport in place across re-renders.
 */
export function useScrollRestore({
    debounceMs = 150,
    behavior = "instant"
}: UseScrollRestoreOptions = {}) {
    const path = usePathname()
    const suppressRef = useRef(false)

    useEffect(() => {
        if (_isPopstateNavigation) {
            const target = readPosition(path)
            if (target && target > 0) {
                window.scrollTo({ top: target, behavior })
            }
        }
        _isPopstateNavigation = false

        let timer: ReturnType<typeof setTimeout>

        const handleScroll = () => {
            if (suppressRef.current) return
            clearTimeout(timer)
            timer = setTimeout(() => {
                const y = Math.ceil(window.scrollY)
                if (y > 0) writePosition(path, y)
            }, debounceMs)
        }

        window.addEventListener("scroll", handleScroll, { passive: true })

        return () => {
            clearTimeout(timer)
            window.removeEventListener("scroll", handleScroll)
        }
    }, [path, debounceMs, behavior])

    const preserveScroll = useCallback(
        async <T>(callback: () => Promise<T>): Promise<T> => {
            const y = window.scrollY
            suppressRef.current = true
            try {
                return await callback()
            } finally {
                requestAnimationFrame(() => {
                    window.scrollTo({ top: y, behavior })
                    suppressRef.current = false
                })
            }
        },
        [behavior]
    )

    const clear = useCallback(() => {
        removePosition(path)
    }, [path])

    return { preserveScroll, clear }
}
